import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Award, CheckCircle, Shield, FileText, ExternalLink } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { certificationsAPI, getUploadUrl } from '../services/api';
import { RichTextDisplay } from '../components/ui/rich-text-display';

// Icon mapping for certification icons
const iconMap = {
  'Award': Award,
  'Shield': Shield,
  'CheckCircle': CheckCircle,
};

export const Certifications = () => {
  const { t } = useTranslation();
  const [certifications, setCertifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadCertifications = async () => {
      try {
        const response = await certificationsAPI.getAll();
        setCertifications(response.data);
      } catch (error) {
        console.error('Error loading certifications:', error);
      } finally {
        setLoading(false);
      }
    };
    loadCertifications();
  }, []);

  const getIcon = (iconName) => {
    return iconMap[iconName] || Award;
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-dark overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-1/4 left-1/3 w-96 h-96 bg-primary rounded-full blur-3xl animate-float"></div>
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl mx-auto text-center"
          >
            <h1 className="font-display font-bold text-5xl lg:text-6xl mb-6 text-white">
              {t('certifications.title')}
            </h1>
            <p className="text-xl text-gray-300">
              {t('certifications.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Certifications Grid */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-pulse text-muted-foreground">Caricamento certificazioni...</div>
            </div>
          ) : certifications.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">{t('certifications.noCertifications', 'Nessuna certificazione disponibile')}</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {certifications.map((cert, index) => {
                const IconComponent = getIcon(cert.icon);
                return (
                  <motion.div
                    key={cert.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card className="glass-card h-full">
                      <CardContent className="pt-6">
                        <div className="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                          <IconComponent className="w-8 h-8 text-primary" />
                        </div>
                        <h3 className="font-display font-semibold text-2xl mb-2 text-foreground">
                          {cert.name}
                        </h3>
                        <div className="text-muted-foreground mb-4">
                          <RichTextDisplay content={cert.description} />
                        </div>
                        {cert.pdf_url && (
                          <Button
                            variant="outline"
                            size="sm"
                            asChild
                            className="mt-2"
                          >
                            <a href={getUploadUrl(cert.pdf_url)} target="_blank" rel="noopener noreferrer">
                              <FileText className="w-4 h-4 mr-2" />
                              Visualizza Certificato
                              <ExternalLink className="w-3 h-3 ml-2" />
                            </a>
                          </Button>
                        )}
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          )}

          {/* Additional Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-16 max-w-3xl mx-auto text-center"
          >
            <Card className="glass-card">
              <CardContent className="pt-6">
                <h3 className="font-display font-semibold text-2xl mb-4 text-foreground">
                  {t('certifications.complianceTitle', 'Compliance & Quality Assurance')}
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  {t('certifications.complianceText', 'All our products and services comply with international standards and regulations. We maintain strict quality control processes and continuous improvement programs to ensure the highest level of safety and reliability for healthcare professionals and patients.')}
                </p>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Certifications;
